import { useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, RotateCcw, Award } from "lucide-react";
import { Link } from "react-router-dom";

export default function QuizView({ courseId, quiz, kind, onCompleted }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (qid, idx) => setAnswers({ ...answers, [qid]: idx });
  const allAnswered = quiz.questions.every((q) => answers[q.question_id] !== undefined);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/quizzes/${quiz.quiz_id}/submit`, { answers });
      setResult(data);
      if (onCompleted) onCompleted(data);
    } finally { setBusy(false); }
  };

  const retry = () => { setAnswers({}); setResult(null); };

  if (result) {
    return (
      <div className="max-w-2xl" data-testid="quiz-result">
        <div className={`p-8 border ${result.passed ? "border-brand-rust bg-white" : "border-brand-line bg-white"}`}>
          {result.passed ? <Award className="w-10 h-10 text-brand-rust" /> : <XCircle className="w-10 h-10 text-brand-inkMuted" />}
          <h2 className="font-display text-4xl mt-4 text-brand-ink">
            {result.passed ? "You passed." : "Not quite."}
          </h2>
          <p className="mt-2 text-brand-inkMuted">
            <span className="font-medium text-brand-ink">{result.score}%</span> ({result.correct} / {result.total}) — need {result.threshold}% to pass
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {result.detail.map((d, i) => (
            <div key={d.question_id} className="bg-white border border-brand-line p-5" data-testid={`quiz-feedback-${i}`}>
              <div className="flex items-start gap-3">
                {d.is_correct
                  ? <CheckCircle2 className="w-5 h-5 text-brand-rust flex-none mt-0.5" />
                  : <XCircle className="w-5 h-5 text-brand-inkMuted flex-none mt-0.5" />}
                <div className="flex-1">
                  <p className="font-serif text-lg text-brand-ink leading-snug">{i + 1}. {d.prompt}</p>
                  <ul className="mt-3 space-y-1 text-sm">
                    {d.choices.map((c, ci) => (
                      <li key={ci} className={`px-3 py-1.5 ${
                        ci === d.correct_index
                          ? "bg-brand-rust/10 text-brand-ink border-l-2 border-brand-rust"
                          : ci === d.chosen
                          ? "bg-brand-bg text-brand-inkMuted line-through"
                          : "text-brand-inkMuted"
                      }`}>{c}{ci === d.correct_index && <span className="overline ml-2 text-brand-rust">correct</span>}</li>
                    ))}
                  </ul>
                  {d.explanation && <p className="mt-3 text-sm text-brand-inkMuted italic">{d.explanation}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          data-testid="quiz-retry-btn"
          onClick={retry}
          className="mt-8 inline-flex items-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors"
        >
          <RotateCcw className="w-4 h-4" /> Retake quiz
        </button>
        {result.passed && kind === "final" && (
          <Link
            to={`/certificate/${courseId}`}
            data-testid="view-certificate-link"
            className="mt-8 ml-3 inline-flex items-center gap-2 bg-brand-rust text-white hover:bg-brand-rustDark px-6 py-3 text-sm font-medium tracking-wide transition-colors"
          >
            <Award className="w-4 h-4" /> View certificate
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="quiz-view">
      <span className="overline text-brand-rust">{kind === "final" ? "Final Exam" : "Module Quiz"}</span>
      <h1 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink leading-[1.1]" data-testid="quiz-title">{quiz.title}</h1>
      <p className="mt-3 overline text-brand-inkMuted">{quiz.questions.length} questions · {quiz.pass_threshold}% to pass</p>

      <div className="mt-10 space-y-6">
        {quiz.questions.map((q, i) => (
          <div key={q.question_id} className="bg-white border border-brand-line p-6" data-testid={`quiz-q-${i}`}>
            <p className="font-serif text-lg text-brand-ink leading-snug">{i + 1}. {q.prompt}</p>
            <ul className="mt-4 space-y-2">
              {q.choices.map((c, ci) => (
                <li key={ci}>
                  <label className={`flex items-start gap-3 p-3 cursor-pointer border transition-colors ${
                    answers[q.question_id] === ci
                      ? "border-brand-ink bg-brand-bg"
                      : "border-brand-line hover:border-brand-ink"
                  }`}>
                    <input
                      type="radio"
                      name={q.question_id}
                      checked={answers[q.question_id] === ci}
                      onChange={() => set(q.question_id, ci)}
                      className="mt-1 accent-brand-rust"
                      data-testid={`quiz-q-${i}-choice-${ci}`}
                    />
                    <span className="text-brand-ink">{c}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <button
        data-testid="quiz-submit-btn"
        disabled={!allAnswered || busy}
        onClick={submit}
        className="mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-50"
      >
        {busy ? "Grading…" : "Submit answers"}
      </button>
    </div>
  );
}
